import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, ReplaySubject, tap, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, AuthResponse } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public currentUser = signal<User | null>(null);

  // Emits once when the initial /auth/me check completes (success or 401).
  // ReplaySubject(1) ensures late subscribers (guards) get the value immediately
  // if init has already finished.
  private authReady$ = new ReplaySubject<void>(1);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.initializeSession();
  }

  /**
   * Called once on startup. Hits /auth/me to restore session from the HttpOnly
   * cookie. Guards wait for this to complete before evaluating auth state.
   */
  private initializeSession(): void {
    this.http
      .get<User>(`${this.API_URL}/auth/me`)
      .pipe(catchError(() => of(null)))
      .subscribe((user) => {
        if (user) {
          this.currentUserSubject.next(user);
          this.currentUser.set(user);
        }
        this.authReady$.next();
        this.authReady$.complete();
      });
  }

  /**
   * Returns an Observable that emits true/false once auth init is done.
   * Guards subscribe to this instead of calling isAuthenticated() directly.
   */
  waitForReady(): Observable<boolean> {
    return this.authReady$.pipe(map(() => this.isAuthenticated()));
  }

  requestAuthCode(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_URL}/auth/request-code`,
      { email },
    );
  }

  verifyAuthCode(email: string, code: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.API_URL}/auth/verify`, { email, code })
      .pipe(
        tap((response) => {
          this.currentUserSubject.next(response.user);
          this.currentUser.set(response.user);
        }),
      );
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.API_URL}/auth/me`).pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
        this.currentUser.set(user);
      }),
    );
  }

  logout(): void {
    this.http.post(`${this.API_URL}/auth/logout`, {}).subscribe({
      complete: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  hasRole(roles: string[]): boolean {
    const user = this.currentUserSubject.value;
    return user ? roles.includes(user.role) : false;
  }

  private clearSession(): void {
    this.currentUserSubject.next(null);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }
}
